import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import * as motion from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { OnProgressProps } from 'react-player/base';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { bg300 } from '@osmosis/tailwind-ui/styles/background';
import { bgPrimary } from '@osmosis/tailwind-ui/styles/background';

import { formatMinutesSeconds } from '@osmosis/shared/time';
import { VideoChapter } from '@osmosis/shared/types/learndb/learnData';

import VideoChapterPoint from './VideoChapterPoint';

import useModal from '../../hooks/useModal';
import { slideVerticalReverse } from '../../styles/animation';

dayjs.extend(duration);

export type VideoProgressBarProps = {
  duration: number;
  handleChangePosition: (value: number) => void;
  progressState: OnProgressProps;
  chapters?: VideoChapter[];
};

const Bar = ({ progress, color }: { color: string; progress: number }) => {
  return (
    <Box
      style={{
        width: `${progress * 100}%`,
      }}
      cn={`absolute pointer-events-none left-0 top-0 h-full rounded-full ${color}`}
    />
  );
};

/**
 * .
 */
const VideoProgressBar = ({
  duration,
  handleChangePosition,
  chapters,
  progressState,
}: VideoProgressBarProps) => {
  const progress = progressState.played;
  const loadedProgress = progressState.loaded;
  const intl = useIntl();

  const tooltip = useModal();
  const [tooltipProgress, setTooltipProgress] = useState(0);
  const tooltipHideTimeout = useRef<NodeJS.Timeout | null>(null);
  const scrubBarRef = useRef(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.target === scrubBarRef.current) {
      const {
        nativeEvent: { offsetX },
        currentTarget,
      } = e;
      const { width } = currentTarget.getBoundingClientRect();
      setTooltipProgress(offsetX / width);
    }
  };

  const handleMouseEnterProgressBar = () => {
    tooltip.show();
    if (tooltipHideTimeout.current) {
      clearTimeout(tooltipHideTimeout.current);
      tooltipHideTimeout.current = null;
    }
  };

  const handleMouseLeaveProgressBar = () => {
    tooltipHideTimeout.current = setTimeout(() => {
      tooltip.hide();
    }, 300);
  };

  const seconds = tooltipProgress * duration;

  return (
    <Box
      cn={`${bg300} w-full h-[10px] relative rounded-full cursor-pointer relative`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnterProgressBar}
      onMouseLeave={handleMouseLeaveProgressBar}
      tabIndex={0}
    >
      <AnimatePresence>
        {tooltip.isVisible && (
          <motion.div
            className="absolute bottom-[25px] z-[10]"
            style={{ left: `${tooltipProgress * 100}%` }}
            data-testid="video-progress-tooltip"
            {...slideVerticalReverse({ animate: { opacity: 0.9 } })}
          >
            <Box cn="relative left-[-50%] bg-slate-800 px-2 py-1 rounded-lg text-white">
              {formatMinutesSeconds(seconds)}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={scrubBarRef}
        type="range"
        data-testid="video-progress-bar"
        min={0}
        max={duration}
        style={{
          width: '100%',
          position: 'absolute',
          height: '100%',
          cursor: 'pointer',
          margin: 0,
          opacity: 0,
        }}
        onChange={(e): void => handleChangePosition(+e.target.value)}
        value={progressState.playedSeconds}
        aria-label={intl.formatMessage({
          id: 'video_control_bar.progress_bar',
          defaultMessage: 'Video progress bar',
        })}
      />

      <Bar
        color={`bg-slate-400/40 dark:bg-slate-500`}
        progress={loadedProgress}
      />

      <Bar color={bgPrimary} progress={progress} />

      {chapters?.map((chapter) => (
        <VideoChapterPoint
          key={chapter.chapterID}
          chapter={chapter}
          duration={duration}
          onClick={handleChangePosition}
          data-testid={`video-chapter-point-${chapter.chapterID}`}
        />
      ))}
    </Box>
  );
};

export default VideoProgressBar;
